import { Controller } from "@hotwired/stimulus";

import MapService from "@/services/mapService";

// Core map initialization and center management
// Works with map-static (upgrade) and map-markers (markers) controllers
//
// Persistence strategy:
// 1. The map element has data-turbo-permanent so it survives Turbo navigation
// 2. State is stored directly on the DOM element (prefixed with _) to survive controller reconnect
// 3. requestAnimationFrame defers initialization until Turbo finishes transplanting
// 4. centerValueChanged handles panning when location changes
export default class MapController extends Controller {
  static targets = ["embed"];
  static outlets = ["map-list", "map-markers", "map-static"];
  static values = {
    provider: String,
    center: Object,
    zoom: Number,
    apiKey: String,
    token: String, // For Apple Maps
    mode: { type: String, default: "static" },
    staticUrl: String,
    markers: Array, // Initial markers to add on load
  };

  get mapService() {
    return this.element._mapService;
  }

  connect() {
    // Defer to allow Turbo to finish transplanting permanent elements
    requestAnimationFrame(() => this.initialize());

    // Clean up map before Turbo caches the page - prevents corrupted state on restore
    this.handleBeforeCache = () => {
      if (this.element._mapService) {
        this.destroyMap();
        // Reset map-list controller's render flag so it will re-render markers
        if (this.hasMapListOutlet) {
          this.mapListOutlet.hasRenderedInitial = false;
        }
      }
    };
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);

    // Also handle browser bfcache (for non-Turbo navigations)
    this.handlePageShow = (event) => {
      if (event.persisted && this.element._mapService) {
        this.destroyMap();
        if (this.hasMapListOutlet) {
          this.mapListOutlet.hasRenderedInitial = false;
        }
        requestAnimationFrame(() => this.initializeInteractiveMap());
      }
    };
    window.addEventListener("pageshow", this.handlePageShow);
  }

  initialize() {
    // No embed target means map not rendered on this page (e.g., pagination page 2+)
    if (!this.hasEmbedTarget) {
      return;
    }

    // Restore state from DOM element (survives turbo-permanent reconnect)
    // Controller instance is new, but DOM element persists with our stored state
    if (this.element._mapService) {
      this.bindMapEvents(); // Rebind events to THIS controller instance

      // Pan if center changed (e.g., via Stimulus value from morph)
      const newCenter = this.centerValue;
      const cachedCenter = this.element._center;
      const hasValidCenter =
        newCenter &&
        Number.isFinite(newCenter.lat) &&
        Number.isFinite(newCenter.lng);
      const centerChanged =
        hasValidCenter &&
        cachedCenter &&
        (cachedCenter.lat !== newCenter.lat ||
          cachedCenter.lng !== newCenter.lng);

      if (centerChanged) {
        this.mapService.setCenter(newCenter.lat, newCenter.lng);
      }
      if (hasValidCenter) {
        this.element._center = newCenter;
      }

      this.signalReady();
      return;
    }

    // Fresh initialization - ensure markers map exists
    if (!this.element._markers) {
      this.element._markers = new Map();
    }

    // Check if user previously engaged with map - skip static mode
    if (this.hasMapStaticOutlet && this.mapStaticOutlet.shouldSkipStatic?.()) {
      this.modeValue = "interactive";
    }

    // Lazy load — observe the embed target, not the controller element (row).
    // The embed lives inside the map column which is d-none on mobile,
    // so observing it skips map loading entirely on small viewports.
    const observed = this.embedTarget;
    if (this.isVisible(observed)) {
      this.load();
    } else {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.load();
            this.intersectionObserver.disconnect();
          }
        },
        { rootMargin: "200px" },
      );
      this.intersectionObserver.observe(observed);
    }
  }

  disconnect() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.handleBeforeCache) {
      document.removeEventListener(
        "turbo:before-cache",
        this.handleBeforeCache,
      );
    }
    if (this.handlePageShow) {
      window.removeEventListener("pageshow", this.handlePageShow);
    }
  }

  // Clean up map instance and DOM - used for bfcache restoration
  destroyMap() {
    // Destroy the map service if it has a destroy method
    this.element._mapService?.destroy?.();

    // Clear stored state
    this.element._mapService = null;
    this.element._markers = null;
    this.element._center = null;
    this.element._eventHandlers = null;
    this.element._dragEndBound = false;
    this.element._initializingPromise = null;
    delete this.element.dataset.mapReady;

    // Clear the embed container (removes mk-map-view elements)
    if (this.hasEmbedTarget) {
      this.embedTarget.innerHTML = "";
    }
  }

  // Stimulus value callback - triggered when data-map-center-value changes (via morphing)
  centerValueChanged(newCenter, oldCenter) {
    if (
      !this.mapService ||
      !Number.isFinite(newCenter?.lat) ||
      !Number.isFinite(newCenter?.lng)
    ) {
      return;
    }

    // Only pan if location actually changed
    if (oldCenter?.lat !== newCenter.lat || oldCenter?.lng !== newCenter.lng) {
      this.mapService.setCenter(newCenter.lat, newCenter.lng);
    }
  }

  isVisible(el) {
    const rect = el.getBoundingClientRect();
    // Zero-size rect means display:none (e.g. mobile d-none column)
    if (rect.width === 0 && rect.height === 0) return false;
    return rect.top < window.innerHeight + 200 && rect.bottom > -200;
  }

  load() {
    if (this.modeValue === "static" && this.staticUrlValue) {
      // Delegate to static controller
      if (this.hasMapStaticOutlet) {
        this.mapStaticOutlet.renderStaticMap();
      }
    } else {
      this.initializeInteractiveMap();
    }
  }

  async initializeInteractiveMap() {
    // Guard against concurrent initialization using DOM element flag
    if (this.element._initializingPromise) {
      await this.element._initializingPromise;
      // After waiting, restore from DOM element
      if (this.element._mapService) {
        this.bindMapEvents();
        return;
      }
    }

    const center =
      this.centerValue?.lat && this.centerValue?.lng ? this.centerValue : null;

    this.element._initializingPromise = (async () => {
      try {
        const mapService = await MapService.create(
          this.providerValue,
          this.embedTarget.id,
          { center: center, zoom: this.zoomValue },
          { apiKey: this.apiKeyValue, token: this.tokenValue },
        );
        // Store on DOM element for persistence across controller reconnects
        this.element._mapService = mapService;
        this.element._center = center;
      } catch (error) {
        console.error("Error initializing map:", error);
        this.element._initializingPromise = null;
        return;
      }
    })();

    await this.element._initializingPromise;
    this.element._initializingPromise = null;

    if (!this.mapService) return;

    this.bindMapEvents();
    this.signalReady();
  }

  bindMapEvents() {
    // Remove old event listeners if they exist (handles controller reconnect)
    const oldHandlers = this.element._eventHandlers;
    if (oldHandlers) {
      this.element.removeEventListener(
        "map:addMarkers",
        oldHandlers.addMarkers,
      );
      this.element.removeEventListener("map:setCenter", oldHandlers.setCenter);
      this.element.removeEventListener(
        "map:setInteraction",
        oldHandlers.setInteraction,
      );
      this.element.removeEventListener(
        "map:highlightMarker",
        oldHandlers.highlightMarker,
      );
      this.element.removeEventListener(
        "map:resetMarkerHighlight",
        oldHandlers.resetMarkerHighlight,
      );
    }

    // Create new bound handlers for THIS controller instance
    // Delegate marker events to map-markers controller (check hasOutlet to avoid throw)
    const handlers = {
      addMarkers: (e) =>
        this.hasMapMarkersOutlet && this.mapMarkersOutlet.addMarkers(e),
      setCenter: this.setCenter.bind(this),
      setInteraction: this.setInteraction.bind(this),
      highlightMarker: (e) =>
        this.hasMapMarkersOutlet && this.mapMarkersOutlet.highlightMarker(e),
      resetMarkerHighlight: (e) =>
        this.hasMapMarkersOutlet &&
        this.mapMarkersOutlet.resetMarkerHighlight(e),
    };
    this.element._eventHandlers = handlers;

    this.element.addEventListener("map:addMarkers", handlers.addMarkers);
    this.element.addEventListener("map:setCenter", handlers.setCenter);
    this.element.addEventListener(
      "map:setInteraction",
      handlers.setInteraction,
    );
    this.element.addEventListener(
      "map:highlightMarker",
      handlers.highlightMarker,
    );
    this.element.addEventListener(
      "map:resetMarkerHighlight",
      handlers.resetMarkerHighlight,
    );

    // Dispatch region change for auto-search functionality (only bind once)
    if (!this.element._dragEndBound) {
      this.element._dragEndBound = true;
      this.mapService.onDragStart?.(() => {
        document.dispatchEvent(new CustomEvent("map:dragstart"));
      });
      this.mapService.onDragEnd?.((data) => {
        document.dispatchEvent(
          new CustomEvent("map:regionchangeend", {
            detail: data,
          }),
        );
      });
    }
  }

  signalReady() {
    // Signal map is ready - use outlet if available, fallback to event
    this.element.dataset.mapReady = "true";

    // Add initial markers if provided via value (for standalone maps like church profile)
    if (this.hasMarkersValue && this.markersValue.length > 0) {
      this.markersValue.forEach((marker) => {
        // Use native marker for simple standalone maps (e.g., church profile)
        if (this.mapService.addSimpleMarker) {
          this.mapService.addSimpleMarker(marker.lat, marker.lng, marker.label);
        } else {
          this.mapService.addMarker(marker.lat, marker.lng, marker.label);
        }
      });
    }

    if (this.hasMapListOutlet) {
      this.mapListOutlet.onMapReady?.();
    }
    this.element.dispatchEvent(new CustomEvent("map:ready", { bubbles: true }));
  }

  signalUpgraded() {
    // Signal to map-list controller to re-render markers - use outlet if available
    if (this.hasMapListOutlet) {
      this.mapListOutlet.onMapUpgraded?.();
    }
    this.element.dispatchEvent(
      new CustomEvent("map:upgraded", { bubbles: true }),
    );
  }

  // Public method - can be called via outlet or event
  setCenter(eventOrCoords) {
    const { lat, lng } = eventOrCoords?.detail || eventOrCoords;
    this.mapService?.setCenter(lat, lng);
  }

  // Enable/disable map scroll and zoom interaction (for mobile bottom sheet)
  setInteraction(eventOrEnabled) {
    const enabled = eventOrEnabled?.detail?.enabled ?? eventOrEnabled;
    this.mapService?.setInteraction?.(enabled);
  }

  // Public methods that delegate to map-markers controller
  // These allow map_list_controller to use a single map outlet
  addMarkers(data) {
    if (this.hasMapMarkersOutlet) {
      this.mapMarkersOutlet.addMarkers(data);
    }
  }

  highlightMarker(itemId) {
    if (this.hasMapMarkersOutlet) {
      this.mapMarkersOutlet.highlightMarker(itemId);
    }
  }

  resetMarkerHighlight(itemId) {
    if (this.hasMapMarkersOutlet) {
      this.mapMarkersOutlet.resetMarkerHighlight(itemId);
    }
  }
}
